import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiServiceBase } from '@api/abstracts';
import { Time } from '@shared/constants';
import { Observable, of, shareReplay, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SvgService extends ApiServiceBase<void> {
  // Memory cache for already fetched SVG content
  private readonly cache = new Map<string, string>();

  // Track ongoing requests to avoid duplicate network calls
  private readonly ongoingRequests = new Map<string, Observable<string>>();

  constructor() {
    super('');
  }

  /**
   * Fetch an SVG as Observable (optimized with cache + shared observable).
   * @param src URL to the SVG asset
   * @returns Observable<string> of SVG content
   */
  get(src: string): Observable<string> {
    // If it's already cached, return it immediately
    if (this.cache.has(src)) {
      return of(this.cache.get(src)!);
    }

    // If a request is already ongoing, return that observable
    if (this.ongoingRequests.has(src)) {
      return this.ongoingRequests.get(src)!;
    }

    // Otherwise, make the request and store it temporarily
    const request$ = this.customRequest<string>({
      method: 'GET',
      externalUrl: src,
      options: {
        responseType: 'text',
        headers: new HttpHeaders({
          Accept: 'image/svg+xml',
        }),
      },
      cache: {
        cacheExpirationTime: Time.TEN_HOURS,
        store: 'ASSETS',
      },
    }).pipe(
      tap((svg) => {
        this.cache.set(src, svg);
        this.ongoingRequests.delete(src);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.ongoingRequests.set(src, request$);
    return request$;
  }
}
